export const SIMULATION_TIMEOUT_MS = 90_000;
export const IMAGE_UPLOAD_MAX_ATTEMPTS = 4;

const IMAGE_UPLOAD_RETRY_DELAYS_MS = [1_000, 3_000, 7_000] as const;

export type TSimulationStage =
  | "validation"
  | "billing_check"
  | "upload_registration"
  | "image_upload"
  | "procedure_lookup"
  | "transform_submission"
  | "metadata_polling"
  | "result_polling"
  | "completed";

export type TSimulationErrorCode =
  | "UPLOAD_REGISTRATION_FAILED"
  | "IMAGE_UPLOAD_FAILED"
  | "PROCEDURE_LOOKUP_FAILED"
  | "TRANSFORM_SUBMISSION_FAILED"
  | "METADATA_POLL_FAILED"
  | "RESULT_POLL_FAILED"
  | "MODEL_REJECTED_IMAGE"
  | "SIMULATION_TIMEOUT"
  | "SIMULATION_CANCELLED"
  | "AUTH_FAILED"
  | "UNKNOWN";

export type TBackendFailureType = "image_validation" | "worker_error";

const errorCodeByStage: Partial<
  Record<TSimulationStage, TSimulationErrorCode>
> = {
  upload_registration: "UPLOAD_REGISTRATION_FAILED",
  image_upload: "IMAGE_UPLOAD_FAILED",
  procedure_lookup: "PROCEDURE_LOOKUP_FAILED",
  transform_submission: "TRANSFORM_SUBMISSION_FAILED",
  metadata_polling: "METADATA_POLL_FAILED",
  result_polling: "RESULT_POLL_FAILED",
};

export class SimulationPipelineError extends Error {
  readonly code: TSimulationErrorCode;
  readonly stage: TSimulationStage;
  readonly httpStatus?: number;
  readonly s3Key?: string;
  readonly s3Host?: string;
  readonly uploadAttempts?: number;
  readonly uploadFailureHistory?: string[];
  readonly backendCode?: string;
  readonly backendFailureType?: TBackendFailureType;
  readonly backendRetryable?: boolean;
  readonly isTimeout: boolean;
  readonly cause?: unknown;

  constructor({
    message,
    code,
    stage,
    httpStatus,
    s3Key,
    s3Host,
    uploadAttempts,
    uploadFailureHistory,
    backendCode,
    backendFailureType,
    backendRetryable,
    isTimeout = false,
    cause,
  }: {
    message: string;
    code: TSimulationErrorCode;
    stage: TSimulationStage;
    httpStatus?: number;
    s3Key?: string;
    s3Host?: string;
    uploadAttempts?: number;
    uploadFailureHistory?: string[];
    backendCode?: string;
    backendFailureType?: TBackendFailureType;
    backendRetryable?: boolean;
    isTimeout?: boolean;
    cause?: unknown;
  }) {
    super(message);
    this.name = "SimulationPipelineError";
    this.code = code;
    this.stage = stage;
    this.httpStatus = httpStatus;
    this.s3Key = s3Key;
    this.s3Host = s3Host;
    this.uploadAttempts = uploadAttempts;
    this.uploadFailureHistory = uploadFailureHistory;
    this.backendCode = backendCode;
    this.backendFailureType = backendFailureType;
    this.backendRetryable = backendRetryable;
    this.isTimeout = isTimeout;
    this.cause = cause;
  }
}

export const getS3KeyFromPresignedUrl = (
  presignedUrl: string,
): string | undefined => {
  try {
    const pathname = new URL(presignedUrl).pathname.replace(/^\/+/, "");
    return pathname ? decodeURIComponent(pathname) : undefined;
  } catch {
    return undefined;
  }
};

const getS3HostFromPresignedUrl = (
  presignedUrl: string,
): string | undefined => {
  try {
    return new URL(presignedUrl).hostname || undefined;
  } catch {
    return undefined;
  }
};

export const getSimulationErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  return String(error);
};

export const getSimulationUserMessage = (
  error: SimulationPipelineError,
): string => {
  if (error.isTimeout) {
    return "The simulation took longer than 90 seconds. Please try again.";
  }
  if (error.backendFailureType === "image_validation") {
    return error.message;
  }
  return "The simulation couldn’t be completed. Please try again.";
};

const getHttpStatus = (message: string): number | undefined => {
  const match = message.match(
    /(?:HTTP(?: Error| error)?(?:! Status:|:)?\s*)(\d{3})/i,
  );
  if (!match) return undefined;
  const status = Number(match[1]);
  return Number.isFinite(status) ? status : undefined;
};

export const isAbortError = (error: unknown): boolean =>
  typeof globalThis.DOMException === "function" &&
  error instanceof globalThis.DOMException
    ? error.name === "AbortError"
    : Boolean(
        error &&
          typeof error === "object" &&
          "name" in error &&
          error.name === "AbortError",
      );

const createAbortError = (): Error => {
  if (typeof globalThis.DOMException === "function") {
    return new globalThis.DOMException(
      "The image upload was cancelled.",
      "AbortError",
    );
  }

  const error = new Error("The image upload was cancelled.");
  error.name = "AbortError";
  return error;
};

const waitForUploadRetry = (
  delayMs: number,
  signal: AbortSignal,
): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(createAbortError());
      return;
    }

    const timeoutId = globalThis.setTimeout(() => {
      signal.removeEventListener("abort", handleAbort);
      resolve();
    }, delayMs);
    const handleAbort = () => {
      globalThis.clearTimeout(timeoutId);
      reject(createAbortError());
    };
    signal.addEventListener("abort", handleAbort, { once: true });
  });

export const isRetryableImageUploadStatus = (status: number): boolean =>
  status === 408 || status === 429 || status >= 500;

type TPutImageWithRetryOptions = {
  putUrl: string;
  image: Blob;
  signal: AbortSignal;
  maxAttempts?: number;
  retryDelaysMs?: readonly number[];
  randomImpl?: () => number;
  onAttempt?: (attempt: UploadAttemptDiagnostic) => void;
  fetchImpl?: typeof fetch;
  waitImpl?: (delayMs: number, signal: AbortSignal) => Promise<void>;
};

export const putImageToPresignedUrlWithRetry = async ({
  putUrl,
  image,
  signal,
  maxAttempts = IMAGE_UPLOAD_MAX_ATTEMPTS,
  retryDelaysMs = IMAGE_UPLOAD_RETRY_DELAYS_MS,
  randomImpl = Math.random,
  onAttempt,
  fetchImpl = globalThis.fetch,
  waitImpl = waitForUploadRetry,
}: TPutImageWithRetryOptions): Promise<Response> => {
  const s3Key = getS3KeyFromPresignedUrl(putUrl);
  const s3Host = getS3HostFromPresignedUrl(putUrl);
  const failureHistory: string[] = [];
  const attempts = Math.max(1, maxAttempts);

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    if (signal.aborted) throw createAbortError();
    const startedAt = Date.now();
    try {
      const response = await fetchImpl(putUrl, {
        method: "PUT",
        headers: {
          "Content-Type": image.type || "image/png",
        },
        body: image,
        signal,
      });

      onAttempt?.({
        attempt,
        durationMs: Date.now() - startedAt,
        httpStatus: response.status,
      });
      if (response.ok) return response;

      failureHistory.push(`attempt ${attempt}: HTTP ${response.status}`);
      const shouldRetry =
        attempt < attempts && isRetryableImageUploadStatus(response.status);
      if (shouldRetry) {
        const delayMs =
          retryDelaysMs[Math.min(attempt - 1, retryDelaysMs.length - 1)] ?? 0;
        await waitImpl(
          Math.round(delayMs * (0.8 + randomImpl() * 0.4)),
          signal,
        );
        continue;
      }

      const responseBody = await response.text().catch(() => "");
      const keyMessage = s3Key ? ` to S3 key "${s3Key}"` : "";
      const bodySuffix = responseBody ? ` - ${responseBody}` : "";
      throw new SimulationPipelineError({
        message: `Failed to upload image${keyMessage} after ${attempt} attempt${attempt === 1 ? "" : "s"}: HTTP Error: ${response.status}${bodySuffix}`,
        code:
          response.status === 401 || response.status === 403
            ? "AUTH_FAILED"
            : "IMAGE_UPLOAD_FAILED",
        stage: "image_upload",
        httpStatus: response.status,
        s3Key,
        s3Host,
        uploadAttempts: attempt,
        uploadFailureHistory: failureHistory,
      });
    } catch (error) {
      if (error instanceof SimulationPipelineError || isAbortError(error)) {
        throw error;
      }

      const errorName = error instanceof Error ? error.name : typeof error;
      onAttempt?.({ attempt, durationMs: Date.now() - startedAt, errorName });
      failureHistory.push(
        `attempt ${attempt}: ${errorName}: ${getSimulationErrorMessage(error)}`,
      );
      if (attempt < attempts) {
        const delayMs =
          retryDelaysMs[Math.min(attempt - 1, retryDelaysMs.length - 1)] ?? 0;
        await waitImpl(
          Math.round(delayMs * (0.8 + randomImpl() * 0.4)),
          signal,
        );
        continue;
      }

      const keyMessage = s3Key ? ` to S3 key "${s3Key}"` : "";
      throw new SimulationPipelineError({
        message: `Failed to upload image${keyMessage} after ${attempt} attempts: ${getSimulationErrorMessage(error)}`,
        code: "IMAGE_UPLOAD_FAILED",
        stage: "image_upload",
        s3Key,
        s3Host,
        uploadAttempts: attempt,
        uploadFailureHistory: failureHistory,
        cause: error,
      });
    }
  }

  throw new Error("Image upload retry loop ended unexpectedly");
};

export const normalizeSimulationError = ({
  error,
  stage,
  timedOut,
}: {
  error: unknown;
  stage: TSimulationStage;
  timedOut: boolean;
}): SimulationPipelineError => {
  if (error instanceof SimulationPipelineError) return error;

  const message = getSimulationErrorMessage(error);

  if (timedOut) {
    return new SimulationPipelineError({
      message: "The simulation took longer than 90 seconds.",
      code: "SIMULATION_TIMEOUT",
      stage,
      isTimeout: true,
      cause: error,
    });
  }

  if (isAbortError(error)) {
    return new SimulationPipelineError({
      message: "The simulation was cancelled.",
      code: "SIMULATION_CANCELLED",
      stage,
      cause: error,
    });
  }

  const httpStatus = getHttpStatus(message);
  return new SimulationPipelineError({
    message,
    code:
      httpStatus === 401 || httpStatus === 403
        ? "AUTH_FAILED"
        : (errorCodeByStage[stage] ?? "UNKNOWN"),
    stage,
    httpStatus,
    cause: error,
  });
};

const createAttemptId = (): string => {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return `simulation-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export type TSimulationDeadline = {
  attemptId: string;
  controller: AbortController;
  signal: AbortSignal;
  timeoutMs: number;
  elapsedMs: () => number;
  remainingMs: () => number;
  isTimedOut: () => boolean;
  finish: () => void;
  cancel: () => void;
};

export const createSimulationDeadline = (
  timeoutMs = SIMULATION_TIMEOUT_MS,
): TSimulationDeadline => {
  const controller = new AbortController();
  const startedAt = Date.now();
  let timedOut = false;
  let finished = false;

  const timeoutId = globalThis.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  const clearDeadline = () => {
    if (finished) return;
    finished = true;
    globalThis.clearTimeout(timeoutId);
  };

  return {
    attemptId: createAttemptId(),
    controller,
    signal: controller.signal,
    timeoutMs,
    elapsedMs: () => Math.max(0, Date.now() - startedAt),
    remainingMs: () => Math.max(0, timeoutMs - (Date.now() - startedAt)),
    isTimedOut: () => timedOut,
    finish: clearDeadline,
    cancel: () => {
      clearDeadline();
      controller.abort();
    },
  };
};

export type UploadAttemptDiagnostic = {
  attempt: number;
  durationMs: number;
  httpStatus?: number;
  errorName?: string;
};

export type ImageUploadDiagnostics = {
  attempts: UploadAttemptDiagnostic[];
  urlAgeMs?: number;
  urlExpiresInMs?: number;
};

export const uploadImageWithDiagnostics = async (
  options: TPutImageWithRetryOptions,
  report: (diagnostics: ImageUploadDiagnostics) => void,
): Promise<void> => {
  const diagnostics: ImageUploadDiagnostics = {
    attempts: [],
  };
  const params = new URL(options.putUrl).searchParams;
  const signedAt = params.get("X-Amz-Date");
  const expires = params.get("X-Amz-Expires");
  if (signedAt && /^\d{8}T\d{6}Z$/.test(signedAt)) {
    const issuedAt = Date.parse(
      signedAt.replace(
        /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/,
        "$1-$2-$3T$4:$5:$6Z",
      ),
    );
    diagnostics.urlAgeMs = Date.now() - issuedAt;
    if (expires)
      diagnostics.urlExpiresInMs =
        Number(expires) * 1000 - diagnostics.urlAgeMs;
  } else if (params.has("Expires")) {
    diagnostics.urlExpiresInMs =
      Number(params.get("Expires")) * 1000 - Date.now();
  }
  try {
    await putImageToPresignedUrlWithRetry({
      ...options,
      onAttempt: (attempt) => diagnostics.attempts.push(attempt),
    });
  } finally {
    report(diagnostics);
  }
};
